import { useState } from "react";
import { motion } from "framer-motion";
import { Screen } from "../components/Screen";
import { Ref } from "../components/Ref";
import { Reveal } from "../components/Reveal";
import { Segmented } from "../components/Segmented";
import { Placeholder } from "../components/Placeholder";
import { CountUp } from "../components/CountUp";
import { useSnak } from "../snak/SnakContext";
type Key = "skin" | "blood" | "nodes" | "viscera";

// MAVORIC, active arm — compartment response rates (Kim et al. 2018). Verify against primary publication.
const COMPARTMENTS: {
  key: Key;
  label: string;
  short: string;
  value: number;
  assessment: string;
}[] = [{
  key: "skin",
  label: "Skin",
  short: "Skin",
  value: 42,
  assessment: "Assessed using the modified Severity Weighted Assessment Tool (mSWAT)."
}, {
  key: "blood",
  label: "Blood",
  short: "Blood",
  value: 68,
  assessment: "Assessed by flow cytometry of circulating Sézary cells in patients with blood involvement."
}, {
  key: "nodes",
  label: "Lymph nodes",
  short: "Nodes",
  value: 17,
  assessment: "Assessed by imaging in patients with lymph-node involvement."
}, {
  key: "viscera",
  label: "Viscera",
  short: "Viscera",
  value: 0,
  assessment: "Few patients had visceral involvement; no visceral responses were reported."
}];
export function P5CloserLook() {
  const {
    explore
  } = useSnak();
  const [active, setActive] = useState<Key>("blood");
  const current = COMPARTMENTS.find(c => c.key === active)!;
  const select = (k: Key) => {
    setActive(k);
    explore();
  };
  return <Screen overline={<>
          MAVORIC · randomised phase 3 trial<Ref n={2} data-id="element-369" data-mp="1tubhihg6qsw7" />
        </>} refTarget={2} footnote={<p data-id="element-370" data-mp="1ysmnui79ul9o">
          Proportion of patients with compartment involvement at baseline who achieved a response in that compartment,
          active arm of MAVORIC (n=186).<Ref n={2} data-id="element-371" data-mp="3vmpvx6jitf7v" /> Evaluable n per compartment:{" "}
          <Placeholder data-id="element-372" data-mp="4ev8r366b96u">insert from source</Placeholder>. Comparator-arm (vorinostat) results are reported in the
          publication. Values to be verified against the primary publication. Different study population and design from
          MINT; not directly comparable.
        </p>} data-id="element-368" data-mp="1os3yx90uoekw">
      <Reveal delay={0.1} data-id="element-373" data-mp="6clljd7kcksd">
        <h1 className="mt-5 font-display text-[32px] font-medium leading-[1.1] tracking-[-0.01em]" data-id="element-374" data-mp="6cvb4zop4pxj">
          Why blood involvement deserves attention.
        </h1>
      </Reveal>

      <Reveal delay={0.25} data-id="element-375" data-mp="faxevmztdwoi">
        <div className="mt-6" data-id="element-376" data-mp="fa5ocql2qp53">
          <Segmented options={COMPARTMENTS.map(c => ({
          value: c.key,
          label: c.short
        }))} value={active} onChange={v => select(v as Key)} data-id="element-377" data-mp="bb6k78762wep" />
        </div>

        <div className="mt-6 flex items-start" data-id="element-378" data-mp="p1a3bqar29zx">
          <span className="font-display text-[112px] font-medium leading-[0.82] tracking-[-0.03em]" data-id="element-379" data-mp="k2go9tfteaxn">
            <span key={active} data-id="element-380" data-mp="yzg1bwtfqvzi">
              <CountUp to={current.value} decimals={0} duration={1.2} data-id="element-381" data-mp="z0r1lmno9llv" />
            </span>
          </span>
          <span className="ml-1 font-display text-[42px] font-medium leading-none" data-id="element-382" data-mp="u2zdl3q5ejmy">%</span>
        </div>
        <p className="overline mt-4 leading-[1.6]" data-id="element-383" data-mp="1dnpw5qhtsw4t">
          {current.label} response, active arm<Ref n={2} data-id="element-384" data-mp="1ho00yztvtgi3" />
        </p>
        <p className="mt-2 text-[13px] leading-relaxed text-[var(--s-muted)]" data-id="element-385" data-mp="13oqvau3ay4sd">{current.assessment}</p>

        <div className="mt-6 space-y-2.5" data-id="element-386" data-mp="18nv0zar0pkdy">
          {COMPARTMENTS.map((c, i) => {
          const on = c.key === active;
          return <button key={c.key} onClick={() => select(c.key)} className={`grid w-full grid-cols-[5.5rem_1fr_2.5rem] items-center gap-3 text-left transition-opacity duration-200 ${on ? "opacity-100" : "opacity-60 hover:opacity-90"}`} data-id="element-387" data-mp="ty5pallatxzf" data-mpi={i}>
                <span className={`text-[13px] ${on ? "font-semibold" : ""}`} data-id="element-388" data-mp="puzhvamga0rd">{c.label}</span>
                <span className="relative h-[6px] bg-[var(--s-track)]" data-id="element-389" data-mp="b260tkrj5ckl">
                  <motion.span className="absolute inset-y-0 left-0 bg-[var(--s-fg)]" initial={{
                width: "0%"
              }} animate={{
                width: `${c.value}%`
              }} transition={{
                duration: 1.4,
                delay: 0.5 + i * 0.1,
                ease: [0.16, 1, 0.3, 1]
              }} data-id="element-390" data-mp="fyo56jiy78we" />
                </span>
                <span className="tabular text-right text-[13px]" data-id="element-391" data-mp="14lgvt965ep6q">{c.value}%</span>
              </button>;
        })}
        </div>
      </Reveal>

      <Reveal delay={0.55} data-id="element-392" data-mp="znno2bp75n4c">
        <div className="mt-8 border-l-2 border-[var(--s-fg)] pl-4" data-id="element-393" data-mp="zjrmbxykqjkr">
          <p className="overline text-[10px]" data-id="element-394" data-mp="vixyf4l4t4rv">
            Guidance · German expert recommendations<Ref n={3} data-id="element-395" data-mp="qm7j4jh5id7i" />
          </p>
          <p className="mt-2 font-display text-[19px] leading-snug" data-id="element-396" data-mp="qn92db8bm60b">
            Expert recommendations highlight the relevance of assessing blood involvement in patients with MF or SS being
            considered for systemic therapy.
          </p>
          <p className="mt-2 text-[12px]" data-id="element-397" data-mp="2g1du3nzvmlgm">
            <Placeholder data-id="element-398" data-mp="2fyjd66g2niry">insert verified key recommendation(s) verbatim with page reference</Placeholder>
          </p>
        </div>
      </Reveal>
    </Screen>;
}